// frontend/src/components/VisionAddModal.js
// Modal component for adding vision data directly in the flowsheet

import React, { useState, useEffect } from 'react';
import {
  Modal,
  Form,
  InputNumber,
  Button,
  Row,
  Col,
  Input,
  Space,
  Typography,
  Alert,
  message,
  Divider
} from 'antd';
import { ClockCircleOutlined } from '@ant-design/icons';
import moment from 'moment';
import axios from 'axios';
import { usePatientChart } from '../context/PatientChartContext';
import MomentDatePicker from './MomentDatePicker.tsx';

const { Title, Text } = Typography;

/**
 * VisionAddModal Component
 * Provides a dedicated interface for adding vision data to the flowsheet
 */
const VisionAddModal = ({
  visible,
  onClose,
  onSave,
  patientId,
  loading = false
}) => {
  const [form] = Form.useForm();
  const [submitLoading, setSubmitLoading] = useState(false);
  const [selectedDateTime, setSelectedDateTime] = useState(moment());
  const { refreshVitals } = usePatientChart();

  // Reset form when modal opens/closes
  useEffect(() => {
    if (visible) {
      form.resetFields();
      setSelectedDateTime(moment());
    }
  }, [visible, form]);

  // Handle form submission
  const handleSave = async () => {
    try {
      setSubmitLoading(true);
      
      // Validate form fields
      const values = await form.validateFields();
      
      // Filter out empty values
      const filteredValues = {};
      Object.keys(values).forEach(key => {
        if (values[key] !== null && values[key] !== undefined && values[key] !== '') {
          filteredValues[key] = values[key];
        }
      });

      if (Object.keys(filteredValues).length === 0) {
        message.warning('Please enter at least one vision parameter.');
        return;
      }

      // Prepare payload for backend - using the biometrics endpoint
      const payload = {
        entryDate: selectedDateTime.toISOString(),
        biometrics: filteredValues
      };

      // Call backend API using the existing biometrics endpoint
      const response = await axios.post(`/patients/${patientId}/flowsheet/biometrics`, payload);

      if (response.data.status === 'success') {
        message.success('Vision data saved successfully!');

        // Refresh vitals banner (in case vision data affects displayed metrics)
        refreshVitals();

        // Call parent onSave callback to refresh flowsheet
        if (onSave) {
          await onSave(); // Wait for the parent callback to complete
        }

        // Close modal and reset form after data refresh
        onClose();
        form.resetFields();
      } else {
        throw new Error(response.data.message || 'Failed to save vision data');
      }
    } catch (error) {
      console.error('Error saving vision data:', error);
      message.error(error.response?.data?.message || error.message || 'Failed to save vision data');
    } finally {
      setSubmitLoading(false);
    }
  };

  // Define vision parameters based on parameters.md
  const visionFields = [
    { name: 'biometrics_vision_visualAcuity_distance_right', label: 'Visual Acuity - Distance Right Eye', inputType: 'Input', placeholder: 'e.g., 20/20, 6/6', rules: [] },
    { name: 'biometrics_vision_visualAcuity_distance_left', label: 'Visual Acuity - Distance Left Eye', inputType: 'Input', placeholder: 'e.g., 20/20, 6/6', rules: [] },
    { name: 'biometrics_vision_visualAcuity_distance_both', label: 'Visual Acuity - Distance Both Eyes', inputType: 'Input', placeholder: 'e.g., 20/20, 6/6', rules: [] },
    { name: 'biometrics_vision_visualAcuity_near_right', label: 'Visual Acuity - Near Right Eye', inputType: 'Input', placeholder: 'e.g., J1, N5', rules: [] },
    { name: 'biometrics_vision_visualAcuity_near_left', label: 'Visual Acuity - Near Left Eye', inputType: 'Input', placeholder: 'e.g., J1, N5', rules: [] },
    { name: 'biometrics_vision_visualAcuity_near_both', label: 'Visual Acuity - Near Both Eyes', inputType: 'Input', placeholder: 'e.g., J1, N5', rules: [] },
    { name: 'biometrics_vision_visualAcuity_pinhole_right', label: 'Visual Acuity - Pinhole Right Eye', inputType: 'Input', placeholder: 'e.g., 20/20, 6/6', rules: [] },
    { name: 'biometrics_vision_visualAcuity_pinhole_left', label: 'Visual Acuity - Pinhole Left Eye', inputType: 'Input', placeholder: 'e.g., 20/20, 6/6', rules: [] },
  ];

  return (
    <Modal
      title={
        <Space>
          <ClockCircleOutlined />
          Add Vision Data
        </Space>
      }
      visible={visible}
      onCancel={onClose}
      footer={[
        <Button key="cancel" onClick={onClose}>
          Cancel
        </Button>,
        <Button
          key="save"
          type="primary"
          loading={submitLoading}
          onClick={handleSave}
          style={{ backgroundColor: '#722ed1', borderColor: '#722ed1' }}
        >
          Save Vision Data
        </Button>,
      ]}
      width={800}
      destroyOnClose
    >
      <div style={{ marginBottom: 16 }}>
        <Alert
          message="Add New Vision Entry"
          description="Enter visual acuity assessment results and eye examination measurements for the selected date and time. These assessments help track visual function and eye health changes over time."
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />
        
        {/* Date/Time Selection */}
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col span={12}>
            <Text strong>Entry Date & Time:</Text>
            <br />
            <MomentDatePicker
              showTime
              value={selectedDateTime}
              onChange={setSelectedDateTime}
              format="YYYY-MM-DD HH:mm"
              style={{ width: '100%', marginTop: 4 }}
              placeholder="Select date and time"
            />
          </Col>
        </Row>
      </div>

      <Form form={form} layout="vertical" name="vision_add_form">
        <Title level={5}>Vision Assessment Parameters</Title>
        
        {/* Distance Vision Tests */}
        <Divider orientation="left">Distance Vision Tests</Divider>
        <Row gutter={16}>
          {visionFields.slice(0, 3).map((field) => (
            <Col xs={12} sm={8} md={6} key={field.name}>
              <Form.Item 
                name={field.name} 
                label={field.label}
                rules={field.rules}
              >
                <Input 
                  placeholder={field.placeholder}
                />
              </Form.Item>
            </Col>
          ))}
        </Row>

        {/* Near Vision Tests */}
        <Divider orientation="left">Near Vision Tests</Divider>
        <Row gutter={16}>
          {visionFields.slice(3, 6).map((field) => (
            <Col xs={12} sm={8} md={6} key={field.name}>
              <Form.Item
                name={field.name}
                label={field.label}
                rules={field.rules}
              >
                <Input
                  placeholder={field.placeholder}
                />
              </Form.Item>
            </Col>
          ))}
        </Row>

        {/* Pinhole Vision Tests */}
        <Divider orientation="left">Pinhole Vision Tests</Divider>
        <Row gutter={16}>
          {visionFields.slice(6).map((field) => (
            <Col xs={12} sm={8} md={6} key={field.name}>
              <Form.Item
                name={field.name}
                label={field.label}
                rules={field.rules}
              >
                <Input
                  placeholder={field.placeholder}
                />
              </Form.Item>
            </Col>
          ))}
        </Row>

        {/* Additional Information */}
        <Divider orientation="left">Test Instructions</Divider>
        <Alert
          message="Vision Test Guidelines"
          description={
            <div>
              <p><strong>Distance Vision:</strong> Test at 20 feet (6 meters) using Snellen chart. Record as fraction (e.g., 20/20, 20/40) or decimal equivalent.</p>
              <p><strong>Near Vision:</strong> Test at 14-16 inches (35-40 cm) using Jaeger chart or near card. Record as Jaeger notation (J1, J2) or point size (N5, N8).</p>
              <p><strong>Pinhole Vision:</strong> Test using pinhole occluder over the eye to eliminate refractive errors. Improves vision if refractive error is present. Record using same Snellen notation as distance vision.</p>
              <p><strong>Testing Order:</strong> Test each eye individually first, then both eyes together. Use corrective lenses if normally worn for distance/near, but remove for pinhole testing.</p>
              <p><strong>Recording Format:</strong> Use standard notation - Snellen fractions for distance and pinhole (20/20), Jaeger or point notation for near (J1, N5).</p>
            </div>
          }
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />

        {/* Vision Chart Reference */}
        <Divider orientation="left">Vision Chart Reference</Divider>
        <Alert
          message="Common Visual Acuity Values"
          description={
            <div>
              <p><strong>Distance Vision (Snellen):</strong> 20/15 (excellent), 20/20 (normal), 20/25, 20/30, 20/40, 20/50, 20/70, 20/100, 20/200</p>
              <p><strong>Near Vision (Jaeger):</strong> J1 (excellent), J2 (normal), J3, J4, J5, J6, J7, J8, J9, J10</p>
              <p><strong>Near Vision (Point):</strong> N5 (excellent), N6 (normal), N8, N10, N12, N14, N18, N24, N36, N48</p>
            </div>
          }
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />
      </Form>
    </Modal>
  );
};

export default VisionAddModal;
